<script>
   /****************************************************************
   * ExtremePlot                                                   *
   * --------------------                                          *
   * shows plot with number of extreme samples for different alpha *
   *****************************************************************/

   import { pchisq } from 'mdatools/distributions';
   import { Vector } from 'mdatools/arrays';
   import { Axes, XAxis, YAxis, Box, Lines, Segments, Legend, Points } from 'svelte-plots-basic/2d';
   import { PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS, LINE_COLORS, FACE_COLORS } from '../../../shared/plots/Defaults.js';

   export let params;                                    // JSON with main plot parameters
   export let res;                                       // JSON with SIMCA results
   export let showGrid = true;                           // logical, show grid or not
   export let xLabel = 'Number of extremes (expected)';  // label for x axis
   export let yLabel = 'Number of extremes (observed)';  // label for y axis
   export let ncomp = 1;                                 // number of components selected by user

   // plot saving parameters
   export let fileName = 'plot-extreme';
   export let pngWidth = PNG_WIDTH;
   export let pngHeight = PNG_HEIGHT;
   export let pngRes = PNG_RES;


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   const CONFIDENCE_AREA_COLOR = "#e0e0e0";

   // local copies of Plot parameters
   //let resID = res ? Object.keys(res)[0] : '';

   // function to compute observed extremes
   function getObserved(res, id, ncomp) {
      const v = res.v[ncomp - 1];
      const n = v.f.length;
      let observed = Vector.zeros(n);
      for (let i = 0; i < n; i++) {
         const pi = pchisq(v.f[i], v.Nf);
         observed = observed.add((alpha.apply(v => 1 - pi < v)));
      }

      const legendItem = {label: id, marker: 1, borderColor: LINE_COLORS[id], faceColor: FACE_COLORS[id]};
      const lineColor = LINE_COLORS[id];
      const faceColor = FACE_COLORS[id];

      return {
         values: observed,
         legendItem,
         lineColor,
         faceColor
      };
   }

   $: resIDs = res ? Object.keys(res) : null;
   $: title = ncomp ? 'Extremes plot (A = ' + ncomp + ')' : '';
   $: nObj = res ? res[Object.keys(res)[0]].nrows : null;

   // compute expected values values
   let expected, Nm, Np, alpha;
   $: if (nObj) {
      alpha = Vector.zeros(nObj);
      expected = Vector.zeros(nObj);
      Nm = Vector.zeros(nObj);
      Np = Vector.zeros(nObj);

      for (let i = 1; i <= nObj; i++) {
         const a = i / nObj;
         const D = 2 * Math.sqrt(i * (1 - a));
         Nm.v[i - 1] = i - D;
         Np.v[i - 1] = i + D;
         alpha.v[i - 1] = a;
         expected.v[i - 1] = i;
      }

   } else {
      expected = null;
      Nm = null;
      Np = null;
      alpha = null;
   }

   let observed, legendItems;
   $: if (resIDs && params && params.resID && ncomp && alpha) {
      observed = params.resID === 'both' ?
         resIDs.map(id => getObserved(res[id], id, ncomp)) :
         [getObserved(res[params.resID], params.resID, ncomp)];
      legendItems = observed.map(o => o.legendItem);
   } else {
      observed = null;
      legendItems = null;
   }

</script>

{#if expected && observed }
<Axes {title} {xLabel} {yLabel}
   {fileName} {pngWidth} {pngHeight} {pngRes}
   downloadLinks="hover"
   margins={MARGINS}
   limX={[-1, nObj + 2]}
   limY={[-1, nObj + 2]}
>

   <Lines xValues={expected} yValues={expected} lineColor={CONFIDENCE_AREA_COLOR} />
   <Lines xValues={expected} yValues={Nm} lineColor={CONFIDENCE_AREA_COLOR} />
   <Lines xValues={expected} yValues={Np} lineColor={CONFIDENCE_AREA_COLOR} />
   <Segments xStart={expected} xEnd={expected} yStart={Nm} yEnd={Np}  lineColor={CONFIDENCE_AREA_COLOR} />

   {#each observed as o}
   <Points xValues={expected} yValues={o.values} borderWidth={1.5} borderColor={o.lineColor} />
   {/each}

   {#if legendItems && legendItems.length > 0}
   <Legend items={legendItems} position='bottomright'/>
   {/if}

   <XAxis {showGrid} slot="xaxis" />
   <YAxis {showGrid} slot="yaxis" />
   <Box slot="box" />
</Axes>
{/if}